import { API_BASE } from './api'
import { ApiFailure, call } from './errors'
import { render } from '../copy'

/**
 * 排班表。浏览器打 Control GET /api/v1/duty/skills（要会话），
 * Control 再用 orchestrator 口令去问 GET /debug/skills。
 * 字段名仍是 Go 默认 JSON（skill.Schedule 无 tag），改字段就是空页。
 */
export interface DebugSkill {
  ID: string
  What: string
  Every: number
  Describe: string
  Kind: string
  Risk: string
  Enabled: boolean
  Rate: number
  Runs: number
  ModelSteps: number
  Degrading: boolean
}

export interface DebugSkillsResponse {
  skills: DebugSkill[]
}

export function debugSkillsURL(): string {
  if (API_BASE.startsWith('http://') || API_BASE.startsWith('https://')) {
    const root = API_BASE.endsWith('/') ? API_BASE : `${API_BASE}/`
    return new URL('duty/skills', root).href
  }
  return '/api/v1/duty/skills'
}

export async function getDebugSkills(): Promise<DebugSkillsResponse> {
  const raw = await call<unknown>('/duty/skills', async () => {
    const response = await globalThis.fetch(debugSkillsURL(), {
      method: 'GET',
      credentials: 'include',
      headers: { accept: 'application/json' },
    })
    const contentType = response.headers.get('content-type') ?? ''
    if (!contentType.includes('json')) {
      return { error: {}, response }
    }
    const body: unknown = await response.json()
    if (!response.ok) {
      return { error: body, response }
    }
    return { data: body, response }
  })
  try {
    return parseDebugSkills(raw)
  } catch {
    throw new ApiFailure('reported', render('error.generic'))
  }
}

export function parseDebugSkills(body: unknown): DebugSkillsResponse {
  if (!body || typeof body !== 'object') {
    throw new Error('debug skills: response is not an object')
  }
  const skills = (body as { skills?: unknown }).skills
  if (!Array.isArray(skills)) {
    throw new Error('debug skills: skills is not an array')
  }
  return {
    skills: skills.map((row, index) => parseDebugSkill(row, index)),
  }
}

function parseDebugSkill(row: unknown, index: number): DebugSkill {
  if (!row || typeof row !== 'object') {
    throw new Error(`debug skills: row ${index} is not an object`)
  }
  const value = row as Record<string, unknown>
  const id = asString(value.ID)
  const what = asString(value.What)
  if (!id || !what) {
    throw new Error(`debug skills: row ${index} missing ID or What`)
  }
  return {
    ID: id,
    What: what,
    Every: asNumber(value.Every),
    Describe: asString(value.Describe),
    Kind: asString(value.Kind),
    Risk: asString(value.Risk),
    Enabled: value.Enabled === true,
    Rate: asNumber(value.Rate),
    Runs: asNumber(value.Runs),
    ModelSteps: asNumber(value.ModelSteps),
    Degrading: value.Degrading === true,
  }
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function asNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

export async function setDebugSkillEnabled(id: string, enabled: boolean): Promise<DebugSkill> {
  const raw = await call<unknown>('/duty/skills/{id}', async () => {
    const response = await globalThis.fetch(`${debugSkillsURL()}/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      body: JSON.stringify({ Enabled: enabled }),
    })
    const contentType = response.headers.get('content-type') ?? ''
    if (!contentType.includes('json')) {
      return { error: {}, response }
    }
    const body: unknown = await response.json()
    if (!response.ok) {
      return { error: body, response }
    }
    return { data: body, response }
  })
  return parseDebugSkill(raw, 0)
}
